import React, { createContext, useContext, useState, useRef } from 'react';

import { ITemplateDataGridSettings } from '../../../entities/IClusters';
import { EDatatableKeys, IDatatableFieldsResult } from '../../../actions/datatableActions';
import { ISortData } from './Sort';
import { EDatatableIds, EFieldsType } from './DataTableTools';
import { IDatatableNavigationContext, INavigationData, INavigator } from './DatatableNavigationTypes';

export interface IDatatableFieldsTypes {
    [key: string]: {
        type: EFieldsType;
        item: any;
        field: string;
    }
}

interface IDatatableContextProps {
    filters: { [key: string]: any };
    sortData: { [key: string]: ISortData[] };
    viewsData: { [key: string]: any };
    fields: { [key: string]: IDatatableFieldsResult[] };
    settings: { [key: string]: ITemplateDataGridSettings };
    facets: {
        [key: string]: {
            [key: string]: {
                facetType: string;
            }[];
        }
    };
    fieldTypes: {
        [key: string]: IDatatableFieldsTypes;
    };
    navigationContext: { [key: string]: IDatatableNavigationContext };
    currentIndexKey: EDatatableKeys;
    selectedViews: { [key: string]: string };
    setFieldTypes: (key: string, value: IDatatableFieldsTypes) => void;
    setSortData: (key: string, value: ISortData[]) => void;
    getSettings: (key: string) => ITemplateDataGridSettings | undefined;
    getFilters: (key: string) => any | undefined;
    getFieldsTypes: (key: string) => IDatatableFieldsTypes | undefined;
    getFacets: (key: string) => {
        [key: string]: {
            facetType: string;
        }[];
    } | undefined;
    setFilters: (key: string, value: any) => void;
    setSettings: (
        key: string,
        value:
            | ITemplateDataGridSettings
            | ((prev: ITemplateDataGridSettings | undefined) => ITemplateDataGridSettings)
    ) => void;
    setViewsData: (key: string, value: any) => void;
    reloadDatatable: (key: string) => void;
    clearContext?: (key: string) => void;
    getViewsData: (key: string) => any | undefined;
    getFields: (key: EDatatableIds) => IDatatableFieldsResult[] | undefined;
    setFields: (key: EDatatableIds, value: IDatatableFieldsResult[]) => void
    setCurrentIndexKey: (value: EDatatableKeys) => void
    setFacets: (key: string, value: {
        [key: string]: {
            facetType: string;
        }[];
    }) => void
    getNavigationContext: (key: string) => IDatatableNavigationContext | undefined;
    setNavigationData: (key: string, data: INavigationData[]) => void;
    setCurrentNavigationRowId: (key: string, rowId: string) => void;
    getPrevNavigationData: (key: string) => INavigationData | undefined;
    getNextNavigationData: (key: string) => INavigationData | undefined;
    getNavigator: (key: string) => INavigator;
    getSortData: (key: string) => ISortData[] | undefined;
    getSelectedView: (key: string) => string | undefined;
    setSelectedView: (key: string, value: string) => void;
}

const DatatableContext = createContext<IDatatableContextProps>(undefined);

export const useDatatableContext = () => {
    const context = useContext(DatatableContext);
    if (!context) {
        throw new Error('useDatatableContext must be used within a DatatableContextProvider');
    }
    return context;
};

export const DatatableContextProvider = ({ children }) => {
    const [filters, setFilters] = useState<{ [key: string]: any }>(undefined);
    const [sortData, setSortData] = useState<{ [key: string]: ISortData[] }>({});
    const [viewsData, setViewsData] = useState<{ [key: string]: any }>({}); // TODO when it will be finish on BE side
    const [settings, setSettings] = useState<{ [key: string]: ITemplateDataGridSettings }>({});
    const [fields, setFields] = useState<{ [key: string]: IDatatableFieldsResult[] }>({});
    const [facets, setFacets] = useState<{ [key: string]: { [key: string]: { facetType: string }[] } }>({});
    const [fieldTypes, setFieldTypes] = useState<{
        [key: string]: {
            [key: string]: {
                type: EFieldsType;
                item: any;
                field: string;
            }
        }
    }>({});
    const [currentIndexKey, setCurrentIndexKey] = useState<EDatatableKeys>();
    const [navigationContext, setNavigationContext] = useState<{ [key: string]: IDatatableNavigationContext }>({});
    const [selectedViews, setSelectedViews] = useState<{ [key: string]: string }>({});

    const controllersRef = useRef<{ [key: string]: { temp: INavigationData[]; currentId?: string } }>({});

    const reloadDatatable = (key: string) => setFilters((prevFilters) => ({
        ...prevFilters,
        [key]: {
            ...prevFilters?.[key],
            _reloadTimestamp: Date.now()
        }
    }));

    const clearContext = (key: string) => {
        setSettings((prevSettings) => {
            const newSettings = { ...prevSettings };
            delete newSettings[key];
            return newSettings;
        });
        setFields((prevFields) => {
            const newFields = { ...prevFields };
            delete newFields[key];
            return newFields;
        });
        setSortData((prevSortData) => {
            const newSortData = { ...prevSortData };
            delete newSortData[key];
            return newSortData;
        });
        setFilters((prevfilters) => {
            const newFilters = { ...prevfilters };
            delete newFilters[key];
            return newFilters;
        });
        setSelectedViews((prevSelectedViews) => {
            const newSelectedViews = { ...prevSelectedViews };
            delete newSelectedViews[key];
            return newSelectedViews;
        });
        setViewsData((prevViewsData) => {
            const newViewsData = { ...prevViewsData };
            delete newViewsData[key];
            return newViewsData;
        });
    };

    const setSettingsHandler = (
        key: string,
        value:
            | ITemplateDataGridSettings
            | ((prev: ITemplateDataGridSettings | undefined) => ITemplateDataGridSettings)
    ) => {
        setSettings((prevSettings) => {
            const prevForKey = prevSettings[key];
            const next =
                typeof value === 'function'
                    ? (value as (p: ITemplateDataGridSettings | undefined) => ITemplateDataGridSettings)(prevForKey)
                    : value;
            return {
                ...prevSettings,
                [key]: next
            };
        });
    };

    const setFiltersHandler = (key: string, value: any) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [key]: value
        }));
    };

    const setViewsDataHandler = (key: string, value: any) => {
        setViewsData((prevViewsData) => ({
            ...prevViewsData,
            [key]: value
        }));
    };

    const setFieldTypesHandler = (key: string, value: {
        [key: string]: {
            type: EFieldsType;
            item: any;
            field: string;
        }
    }) => {
        setFieldTypes((prevFieldTypes) => ({
            ...prevFieldTypes,
            [key]: value
        }));
    };

    const setFacetsHandler = (key: string, value: {
        [key: string]: {
            facetType: string;
        }[]
    }) => {
        setFacets((prevFacets) => ({
            ...prevFacets,
            [key]: value
        }));
    };

    const setSortDataHandler = (key: string, value: ISortData[]) => {
        setSortData((prevSortData) => ({
            ...prevSortData,
            [key]: value
        }));
    };

    const getSortData = (key: string): ISortData[] | undefined => {
        return sortData[key];
    };

    const getFilters = (key: string): any | undefined => {
        return filters?.[key];
    };

    const setFieldsHandler = (key: string, value: IDatatableFieldsResult[]) => {
        setFields((prevFields) => ({
            ...prevFields,
            [key]: value
        }));
    };

    const getSettings = (key: string): ITemplateDataGridSettings | undefined => {
        return settings[key];
    };

    const getFields = (key: string): IDatatableFieldsResult[] | undefined => {
        return fields[key];
    };

    const getFieldsTypes = (key: string): IDatatableFieldsTypes | undefined => {
        return fieldTypes[key];
    };

    const getFacets = (key: string): {
        [key: string]: {
            facetType: string;
        }[]
    } | undefined => {
        return facets[key];
    };

    const getViewsData = (key: string): any | undefined => {
        return viewsData[key];
    };

    const getNavigationContext = (key: string): IDatatableNavigationContext | undefined => {
        return navigationContext[key];
    };

    const setNavigationData = (key: string, data: INavigationData[]) => {
        setNavigationContext((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                navigationData: data,
                currentIndex: prev[key]?.currentIndex || 0,
                canNavigatePrev: data.length > 0 && prev[key]?.currentRowId !== data[0]?.id,
                canNavigateNext: data.length > 0 && prev[key]?.currentRowId !== data[data.length - 1]?.id
            }
        }));

        if (controllersRef.current[key]) {
            controllersRef.current[key].temp = [];
        }
    };

    const setCurrentNavigationRowId = (key: string, rowId: string) => {
        const navContext = navigationContext[key];
        if (!navContext) return;

        const newIndex = navContext.navigationData.findIndex(item => item.rowId === rowId);
        if (newIndex !== -1) {
            setNavigationContext((prev) => ({
                ...prev,
                [key]: {
                    ...prev[key],
                    currentRowId: rowId,
                    currentIndex: newIndex,
                    canNavigatePrev: newIndex > 0,
                    canNavigateNext: newIndex < (prev[key]?.navigationData?.length || 0) - 1
                }
            }));
        }
    };

    const getPrevNavigationData = (key: string): INavigationData | undefined => {
        const navContext = navigationContext[key];
        if (!navContext || navContext.currentIndex <= 0) return undefined;
        return navContext.navigationData[navContext.currentIndex - 1];
    };

    const getNextNavigationData = (key: string): INavigationData | undefined => {
        const navContext = navigationContext[key];
        if (!navContext || navContext.currentIndex >= navContext.navigationData.length - 1) return undefined;
        return navContext.navigationData[navContext.currentIndex + 1];
    };

    const getNavigator = (key: string): INavigator => {
        if (!controllersRef.current[key]) {
            controllersRef.current[key] = { temp: [], currentId: undefined };
        }
        const ctrl = controllersRef.current[key];

        const base = () => navigationContext[key]?.navigationData || [];

        const effective = () => [...ctrl.temp, ...base()];
        const resolveCurrentId = () => ctrl.currentId || navigationContext[key]?.currentRowId;
        const currentIndex = () => {
            const id = resolveCurrentId();
            if (!id) return -1;
            return effective().findIndex(item => item.id === id);
        };
        const currentItem = () => {
            const id = resolveCurrentId();
            if (!id) return undefined;
            return effective().find(item => item.id === id);
        };
        const hasPrev = () => {
            const idx = currentIndex();
            return idx > 0;
        };
        const hasNext = () => {
            const eff = effective();
            const idx = currentIndex();
            return idx !== -1 && idx < eff.length - 1;
        };
        const prev = () => {
            const idx = currentIndex();
            if (idx <= 0) return undefined;
            return effective()[idx - 1];
        };
        const next = () => {
            const eff = effective();
            const idx = currentIndex();
            if (idx === -1 || idx >= eff.length - 1) return undefined;
            return eff[idx + 1];
        };
        const setCurrent = (id: string) => {
            ctrl.currentId = id;
        };
        const addTemporary = (item: INavigationData) => {
            ctrl.temp = [...ctrl.temp, item];
        };
        const removeTemporary = (itemId: string) => {
            ctrl.temp = ctrl.temp.filter(i => i.id !== itemId);
        };
        const index = () => Math.max(0, currentIndex());
        const total = () => effective().length;

        return {
            hasPrev,
            hasNext,
            prev,
            next,
            current: currentItem,
            index,
            total,
            setCurrent,
            addTemporary,
            removeTemporary
        };
    };

    const getSelectedView = (key: string): string | undefined => {
        return selectedViews[key];
    };

    const setSelectedView = (key: string, value: string) => {
        setSelectedViews((prevSelectedViews) => {
            const nextSelectedViews = { ...prevSelectedViews };
            if (value === undefined) {
                delete nextSelectedViews[key];
            } else {
                nextSelectedViews[key] = value;
            }
            return nextSelectedViews;
        });
    };

    const initState: IDatatableContextProps = {
        filters,
        sortData,
        viewsData,
        fields,
        settings,
        facets,
        fieldTypes,
        navigationContext,
        currentIndexKey,
        selectedViews,
        getSelectedView,
        setSelectedView,
        getViewsData,
        getSettings,
        setSettings: setSettingsHandler,
        setViewsData: setViewsDataHandler,
        setFilters: setFiltersHandler,
        setFacets: setFacetsHandler,
        setSortData: setSortDataHandler,
        getSortData,
        getFilters,
        reloadDatatable,
        clearContext,
        getFacets,
        getFields,
        setFieldTypes: setFieldTypesHandler,
        getFieldsTypes,
        setFields: setFieldsHandler,
        setCurrentIndexKey,
        getNavigationContext,
        setNavigationData,
        setCurrentNavigationRowId,
        getPrevNavigationData,
        getNextNavigationData,
        getNavigator
    };

    return (
        <DatatableContext.Provider
            value={initState}
        >
            {children}
        </DatatableContext.Provider>
    );
};